import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import API from '../utils/api';
import recordActivity from '../utils/activity';
import { trackDashboardGenerateCta } from '../utils/analytics';

// AI-powered story ideas for the dashboard — 3 GPT-curated concepts based on
// the child's ratings and unexplored interests, plus trending community
// stories for similar-age kids. One tap generates the chosen concept.
const RecommendationsCard = ({ childId }) => {
  const navigate = useNavigate();
  const [recs, setRecs] = useState(null);
  const [generating, setGenerating] = useState(null); // index of concept being generated
  const [error, setError] = useState('');

  useEffect(() => {
    if (!childId) return;
    setRecs(null);
    setError('');
    API.get(`/stories/recommendations/${childId}`)
      .then(res => setRecs(res.data.recommendations))
      .catch(() => {}); // card simply doesn't render
  }, [childId]);

  if (!recs || !recs.concepts?.length) return null;

  const handleGenerate = async (concept, index) => {
    if (generating !== null) return;
    setGenerating(index);
    setError('');
    try {
      const res = await API.post('/stories/generate', {
        childId: recs.childId,
        themes: concept.theme ? [concept.theme] : [],
        interests: concept.interest ? [concept.interest] : [],
        customPrompt: concept.concept.slice(0, 500),
        language: 'en',
      });
      // Reinforcement loop: this recommendation converted to a generation
      recordActivity('recommendation.generated', { childId: recs.childId, title: concept.title, theme: concept.theme }, recs.childId);
      trackDashboardGenerateCta();
      if (res.data.pendingApproval) {
        navigate('/dashboard/approval-queue?new=true');
      } else {
        navigate(`/dashboard/story/${res.data.storyId}?new=true&generating=true`);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Story generation failed. Please try again.');
      setGenerating(null);
    }
  };

  return (
    <div className="rounded-4 p-4 mb-4" style={{background:'var(--card-bg)', border:'1px solid var(--border-purple)'}}>
      <div className="d-flex align-items-center justify-content-between mb-3">
        <div className="fw-bold" style={{color:'var(--text-main)', fontSize:'17px', fontFamily:"'Fredoka One',cursive"}}>
          🪄 Story ideas for {recs.childName}
        </div>
        {recs.source === 'ai' && (
          <span className="badge rounded-pill" style={{background:'var(--badge-purple-bg)', color:'var(--text-main)', fontSize:'11px', padding:'5px 10px'}}>
            ✨ AI-picked
          </span>
        )}
      </div>

      {error && (
        <div className="alert alert-danger py-2 small rounded-3 d-flex align-items-center gap-2 mb-3">
          <i className="bi bi-exclamation-circle-fill"></i>{error}
        </div>
      )}

      <div className="d-flex flex-column gap-2">
        {recs.concepts.map((c, i) => (
          <div key={i} className="d-flex flex-column flex-md-row align-items-md-center gap-2 p-3 rounded-3" style={{background:'var(--item-bg)'}}>
            <div className="flex-grow-1">
              <div className="fw-bold" style={{color:'var(--text-main)', fontSize:'14px'}}>{c.title}</div>
              <div style={{color:'var(--text-muted2)', fontSize:'13px', lineHeight:1.5}}>{c.concept}</div>
              {c.why && (
                <div className="fst-italic" style={{color:'var(--text-soft)', fontSize:'12px', marginTop:'2px'}}>
                  Why: {c.why}
                </div>
              )}
            </div>
            <button
              type="button"
              className="btn btn-dream btn-sm rounded-pill px-3 fw-semibold flex-shrink-0"
              onClick={() => handleGenerate(c, i)}
              disabled={generating !== null}
            >
              {generating === i
                ? <><span className="spinner-border spinner-border-sm me-1" style={{width:'12px', height:'12px', borderWidth:'2px'}}></span>Creating…</>
                : <>✨ Generate</>}
            </button>
          </div>
        ))}
      </div>

      {recs.trending?.length > 0 && (
        <div className="mt-3 pt-3" style={{borderTop:'1px solid var(--row-sep)'}}>
          <div className="fw-semibold mb-2" style={{color:'var(--text-muted2)', fontSize:'13px'}}>
            🔥 Trending for {recs.childAge ? `${recs.childAge}-year-olds` : 'kids'} in the community
          </div>
          <div className="d-flex flex-wrap gap-2">
            {recs.trending.map(t => (
              <Link key={t.slug} to={`/community/${t.slug}`}
                className="badge rounded-pill text-decoration-none"
                style={{background:'var(--purple-section)', color:'var(--text-main)', fontSize:'12px', fontWeight:500, padding:'7px 12px'}}>
                📖 {t.title}{t.likes > 0 ? ` · ❤️ ${t.likes}` : ''}
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default RecommendationsCard;
